<template>
  <v-container class="ninbox pa-0" :style="rootStyle">
    <page-skeleton v-if="loading" variant="list" class="mt-6" />

    <template v-else>
      <!-- ===== Cabeçalho ===== -->
      <header class="ninbox__head">
        <div>
          <div class="ninbox__eyebrow">{{ $t("inbox.eyebrow") }}</div>
          <h1 class="ninbox__title">{{ title || $t("inbox.title") }}</h1>
          <p class="ninbox__sub">{{ subtitle || $t("inbox.subtitle") }}</p>
        </div>

        <span
          class="ninbox__count"
          :class="{ 'ninbox__count--zero': !unreadCount }"
        >
          <span class="ninbox__count-dot"></span>
          {{ $tc("inbox.unread", unreadCount, { count: unreadCount }) }}
        </span>
      </header>

      <!-- ===== Categorias ===== -->
      <!-- As abas vêm da API: a lista de categorias e quem enxerga cada uma
           mora no backend, então adicionar categoria não passa por aqui. -->
      <nav
        v-if="categoryTabs.length > 1"
        class="ncats"
        :aria-label="$t('inbox.categoriesAria')"
      >
        <button
          v-for="tab in categoryTabs"
          :key="tab.key || 'all'"
          type="button"
          class="ncat"
          :class="{ 'ncat--active': tab.key === activeCategory }"
          :aria-pressed="tab.key === activeCategory ? 'true' : 'false'"
          @click="$emit('category-change', tab.key)"
        >
          <v-icon size="15" class="ncat__icon">{{ tab.icon }}</v-icon>
          <span class="ncat__label">{{ tab.label }}</span>
          <span v-if="tab.unread" class="ncat__badge">{{ tab.unread }}</span>
        </button>
      </nav>

      <!-- ===== Inbox ===== -->
      <div class="ninbox__shell">
        <!-- Lista -->
        <aside class="ninbox__pane ninbox__pane--list">
          <div class="ninbox__pane-head">
            <div class="ninbox__pane-title">{{ $t("inbox.inboxTitle") }}</div>
            <div class="ninbox__pane-sub">{{ $t("inbox.inboxSub") }}</div>
            <!-- Retorno de 30/10: filtro de não lidas (aluno e professor). -->
            <button
              type="button"
              class="nfilter"
              :class="{ 'nfilter--active': unreadOnly }"
              :aria-pressed="unreadOnly ? 'true' : 'false'"
              @click="$emit('unread-only-change', !unreadOnly)"
            >
              <v-icon size="14" class="nfilter__icon">{{
                unreadOnly ? "mdi-email-outline" : "mdi-filter-variant"
              }}</v-icon>
              {{ $t("inbox.filterUnread") }}
            </button>
          </div>

          <div v-if="notifications.length" class="ninbox__list">
            <button
              v-for="item in notifications"
              :key="item.id"
              type="button"
              class="nrow"
              :class="{
                'nrow--selected':
                  selectedNotification && selectedNotification.id === item.id,
                'nrow--unread': !item.read,
              }"
              @click="$emit('select', item)"
            >
              <span class="nrow__rail"></span>
              <span class="nrow__body">
                <span class="nrow__top">
                  <span class="nrow__title">
                    <span v-if="!item.read" class="nrow__dot"></span>
                    <span
                      v-if="item.communication && item.communication.pinned"
                      :title="$t('notifications.pinnedTitle')"
                      >📌</span
                    >
                    {{ item.title }}
                  </span>
                  <span class="nrow__date">{{ item.date }}</span>
                </span>
                <span class="nrow__bottom">
                  <span class="nrow__snippet">{{
                    snippet(item.description)
                  }}</span>
                  <span v-if="item.categoryLabel" class="nrow__cat">
                    <v-icon size="12">{{ item.categoryIcon }}</v-icon>
                    {{ item.categoryLabel }}
                  </span>
                  <span v-if="item.voting" class="nrow__tag">{{
                    $t("inbox.voting")
                  }}</span>
                  <!-- Card 86akqxu0h: status do formulário de pesquisa. -->
                  <span v-if="surveyBadge(item)" class="nrow__tag">{{
                    surveyBadge(item)
                  }}</span>
                </span>
              </span>
            </button>
          </div>

          <div v-else class="nempty">
            <v-icon size="34" :color="theme.primary">mdi-bell-outline</v-icon>
            <div class="nempty__title">
              {{
                unreadOnly
                  ? $t("inbox.emptyUnreadTitle")
                  : $t("inbox.emptyTitle")
              }}
            </div>
            <div class="nempty__text">
              {{
                unreadOnly ? $t("inbox.emptyUnreadText") : $t("inbox.emptyText")
              }}
            </div>
          </div>
        </aside>

        <!-- Preview -->
        <section class="ninbox__pane ninbox__pane--preview">
          <div v-if="selectedNotification" class="npv">
            <div class="npv__head">
              <div class="npv__head-main">
                <div class="npv__title">{{ selectedNotification.title }}</div>
                <div class="npv__meta">{{ selectedNotification.date }}</div>
              </div>
              <div class="npv__flags">
                <span
                  class="npv__status"
                  :class="
                    selectedNotification.read
                      ? 'npv__status--read'
                      : 'npv__status--unread'
                  "
                >
                  {{
                    selectedNotification.read
                      ? $t("inbox.read")
                      : $t("inbox.unreadStatus")
                  }}
                </span>
                <button
                  type="button"
                  class="npv__toggle"
                  @click="$emit('toggle-read', selectedNotification)"
                >
                  <v-icon size="14" class="npv__toggle-icon">{{
                    selectedNotification.read
                      ? "mdi-email-outline"
                      : "mdi-email-open-outline"
                  }}</v-icon>
                  {{
                    selectedNotification.read
                      ? $t("inbox.markUnread")
                      : $t("inbox.markRead")
                  }}
                </button>
              </div>
            </div>

            <div v-if="surveyBadge(selectedNotification)" class="npv__survey">
              {{
                $t("inbox.survey", {
                  status: surveyBadge(selectedNotification),
                })
              }}
            </div>

            <!-- Comunicação da escola (card 86akj8ptp): imagem acima do texto,
                 botão abaixo. -->
            <communication-content
              v-if="selectedNotification.communication"
              part="media"
              :notification-id="selectedNotification.id"
              :communication="selectedNotification.communication"
            />

            <div class="npv__body">
              <template v-for="(block, index) in descriptionBlocks">
                <blockquote
                  v-if="block.quote"
                  :key="`q-${index}`"
                  class="npv__quote"
                >
                  <span class="npv__quote-label">{{ block.label }}</span>
                  <span class="npv__quote-text">{{ block.text }}</span>
                </blockquote>
                <linkified-text
                  v-else
                  :key="`p-${index}`"
                  tag="p"
                  class="npv__paragraph"
                  :text="block.text"
                />
              </template>
            </div>

            <communication-content
              v-if="selectedNotification.communication"
              part="action"
              :notification-id="selectedNotification.id"
              :communication="selectedNotification.communication"
              :color="theme.primary"
            />

            <!-- Votação -->
            <div v-if="selectedNotification.voting" class="ncta">
              <div class="ncta__title">{{ $t("inbox.votingTitle") }}</div>
              <div class="ncta__text">
                {{ $t("inbox.votingText") }}
              </div>

              <div class="ncta__actions">
                <v-btn
                  dark
                  rounded
                  depressed
                  class="ncta__btn"
                  :color="theme.primary"
                  :disabled="voted || votingBusy"
                  :loading="votingBusy"
                  @click="
                    $emit('vote', {
                      voteId: selectedNotification.voting,
                      option: true,
                    })
                  "
                >
                  <v-icon small left>mdi-thumb-up-outline</v-icon>
                  {{ $t("inbox.agree") }}
                </v-btn>

                <v-btn
                  rounded
                  outlined
                  class="ncta__btn"
                  :color="theme.primary"
                  :disabled="voted || votingBusy"
                  @click="
                    $emit('vote', {
                      voteId: selectedNotification.voting,
                      option: false,
                    })
                  "
                >
                  <v-icon small left>mdi-thumb-down-outline</v-icon>
                  {{ $t("inbox.disagree") }}
                </v-btn>
              </div>

              <div v-if="voted" class="ncta__feedback">
                <v-icon size="16" color="#00A379">mdi-check</v-icon>
                {{ $t("inbox.voted") }}
              </div>
            </div>

            <!-- Aulas extras no fim do nível (card 86ajzet19). Três passos no
                 mesmo lugar: decidir, escolher a quantidade, conferir a prévia
                 antes de criar. O professor não precisa selecionar turma,
                 alunos ou nível — o sistema já sabe. -->
            <div v-if="extraLessonsPrompt" class="ncta">
              <!-- Retorno de 15/09: respondida, a pergunta se encerra. O estado
                   vem do servidor, então reabrir a caixa de entrada não traz
                   os botões de volta. -->
              <template v-if="extraLessonsDecision">
                <div class="ncta__title">{{ $t("inbox.decided") }}</div>
                <div class="ncta__feedback">
                  <v-icon size="16" color="#00A379">mdi-check</v-icon>
                  {{ extraLessonsDecisionText }}
                </div>
              </template>

              <template v-else-if="!extraLessonsPreview">
                <div class="ncta__title">{{ $t("inbox.extraQuestion") }}</div>
                <div class="ncta__text">
                  {{ $t("inbox.extraText") }}
                </div>

                <div class="ncta__actions">
                  <v-btn
                    rounded
                    outlined
                    class="ncta__btn"
                    :color="theme.primary"
                    :disabled="extraLessonsBusy"
                    @click="
                      $emit('decline-extra-lessons', {
                        promptId: extraLessonsPrompt.promptId,
                      })
                    "
                  >
                    {{ $t("inbox.extraNo") }}
                  </v-btn>
                </div>

                <div class="ncta__hint mt-3">{{ $t("inbox.extraYes") }}</div>
                <div class="ncta__actions">
                  <v-btn
                    v-for="option in extraLessonQuantities"
                    :key="option"
                    rounded
                    depressed
                    dark
                    small
                    class="ncta__btn"
                    :color="theme.primary"
                    :disabled="extraLessonsBusy"
                    :loading="extraLessonsBusy && pendingQuantity === option"
                    @click="requestExtraLessonsPreview(option)"
                  >
                    {{ $t("inbox.extraQty", { count: option }) }}
                  </v-btn>

                  <v-text-field
                    v-model.number="customQuantity"
                    type="number"
                    min="1"
                    max="40"
                    dense
                    outlined
                    hide-details
                    style="max-width: 130px"
                    :label="$t('inbox.other')"
                    :disabled="extraLessonsBusy"
                    @keyup.enter="requestExtraLessonsPreview(customQuantity)"
                  />
                  <v-btn
                    rounded
                    text
                    small
                    class="ncta__btn"
                    :color="theme.primary"
                    :disabled="!validCustomQuantity || extraLessonsBusy"
                    @click="requestExtraLessonsPreview(customQuantity)"
                  >
                    {{ $t("inbox.seeDates") }}
                  </v-btn>
                </div>
              </template>

              <template v-else>
                <div class="ncta__title">
                  {{
                    $tc("inbox.programming", extraLessonsPreview.length, {
                      count: extraLessonsPreview.length,
                    })
                  }}
                </div>

                <!-- Prévia é confirmação, não decoração: o professor precisa
                     ver data a data antes de virar evento no calendário. -->
                <ul class="xtra__list">
                  <li
                    v-for="slot in extraLessonsPreview"
                    :key="`${slot.date}-${slot.startHour}`"
                    class="xtra__item"
                  >
                    <span class="xtra__date">{{ slotLabel(slot) }}</span>
                    <span v-if="slot.subject" class="xtra__subject">{{
                      slot.subject.title
                    }}</span>
                    <span v-else class="xtra__subject xtra__subject--empty">{{
                      $t("inbox.subjectTbd")
                    }}</span>
                  </li>
                </ul>

                <div v-if="extraLessonsIncomplete" class="ncta__hint">
                  {{ $t("inbox.incomplete") }}
                </div>

                <div class="ncta__actions">
                  <v-btn
                    rounded
                    depressed
                    dark
                    class="ncta__btn"
                    :color="theme.primary"
                    :loading="extraLessonsBusy"
                    :disabled="extraLessonsBusy"
                    @click="
                      $emit('confirm-extra-lessons', {
                        promptId: extraLessonsPrompt.promptId,
                        classId: extraLessonsPrompt.classId,
                        quantity: extraLessonsPreview.length,
                      })
                    "
                  >
                    {{ $t("inbox.confirmCreate") }}
                  </v-btn>
                  <v-btn
                    rounded
                    text
                    class="ncta__btn"
                    :disabled="extraLessonsBusy"
                    @click="$emit('reset-extra-lessons')"
                  >
                    {{ $t("common.back") }}
                  </v-btn>
                </div>
              </template>
            </div>

            <!-- Pedido de entrada numa turma (card 86akj7x9f): o professor
                 decide na página da turma, então a notificação leva até ela. -->
            <div v-if="groupRequestClassId" class="ncta">
              <div class="ncta__title">{{ $t("inbox.joinRequest") }}</div>
              <div class="ncta__text">
                {{ $t("inbox.joinRequestText") }}
              </div>

              <div class="ncta__actions">
                <v-btn
                  dark
                  rounded
                  depressed
                  class="ncta__btn"
                  :color="theme.primary"
                  @click="openRequestedGroup"
                >
                  <v-icon small left>mdi-account-group-outline</v-icon>
                  {{ $t("inbox.openClass") }}
                </v-btn>
              </div>
            </div>

            <!-- Pedido recusado ou aluno removido da turma: leva de volta
                 ao match (cards 17trt6jcy4t e 17trt6jda82). Vale também para
                 as recusas avisadas antes deste botão existir. -->
            <div v-if="backToMatch" class="ncta">
              <div class="ncta__title">{{ $t("inbox.nextClass") }}</div>
              <div class="ncta__text">
                {{ $t("inbox.nextClassText") }}
              </div>

              <div class="ncta__actions">
                <v-btn
                  dark
                  rounded
                  depressed
                  class="ncta__btn"
                  :color="theme.primary"
                  @click="openMatch"
                >
                  <v-icon small left>mdi-account-search-outline</v-icon>
                  {{ $t("inbox.goToMatch") }}
                </v-btn>
              </div>
            </div>

            <!-- Retorno de 30/10 (card 17trt6jg2zy): subir de nível mostra a
                 imagem de comemoração do nível e o botão Compartilhar. -->
            <div v-if="levelUpImage" class="ncta ncta--level">
              <img
                :src="levelUpImage"
                :alt="$t('inbox.levelUpAlt')"
                class="ncta__level-img"
              />
              <div class="ncta__actions">
                <v-btn
                  dark
                  rounded
                  depressed
                  class="ncta__btn"
                  :color="theme.primary"
                  :loading="sharingLevel"
                  @click="shareLevelUp"
                >
                  <v-icon small left>mdi-share-variant-outline</v-icon>
                  {{ $t("inbox.shareLevelUp") }}
                </v-btn>
              </div>
            </div>

            <!-- Retorno de 30/10 (card 17trt6jfxt2): presença pendente leva à
                 gestão da turma, onde o professor confirma a chamada. -->
            <div v-if="attendancePendingClassId" class="ncta">
              <div class="ncta__actions">
                <v-btn
                  dark
                  rounded
                  depressed
                  class="ncta__btn"
                  :color="theme.primary"
                  @click="openAttendanceClass"
                >
                  <v-icon small left>mdi-account-check-outline</v-icon>
                  {{ $t("inbox.openClassManagement") }}
                </v-btn>
              </div>
            </div>

            <!-- Retorno de 27/10 (card 86akj8q08): plano de estudos novo ou
                 texto corrigido levam direto ao plano. -->
            <div v-if="isStudyPlanNotice" class="ncta">
              <div class="ncta__actions">
                <v-btn
                  dark
                  rounded
                  depressed
                  class="ncta__btn"
                  :color="theme.primary"
                  @click="openStudyPlan"
                >
                  <v-icon small left>mdi-map-marker-path</v-icon>
                  {{ $t("inbox.openStudyPlan") }}
                </v-btn>
              </div>
            </div>

            <!-- Conteúdo extra indicado pelo professor: leva direto à lista
                 "Indicados pelo professor" (card 17trt6jfxtn). -->
            <div v-if="isTeacherExtraNotice" class="ncta">
              <div class="ncta__actions">
                <v-btn
                  dark
                  rounded
                  depressed
                  class="ncta__btn"
                  :color="theme.primary"
                  @click="openTeacherExtras"
                >
                  <v-icon small left>mdi-book-plus-outline</v-icon>
                  {{ $t("inbox.openTeacherExtras") }}
                </v-btn>
              </div>
            </div>

            <!-- Anotação compartilhada pelo professor: leva direto ao
                 material onde ela está ancorada. Card 86ak5yua7. -->
            <div v-if="teacherNoteContentId" class="ncta">
              <div class="ncta__title">{{ $t("inbox.teacherNote") }}</div>
              <div class="ncta__text">
                {{ $t("inbox.teacherNoteText") }}
              </div>

              <div class="ncta__actions">
                <v-btn
                  dark
                  rounded
                  depressed
                  class="ncta__btn"
                  :color="theme.primary"
                  @click="openTeacherNoteLesson"
                >
                  <v-icon small left>mdi-book-open-page-variant-outline</v-icon>
                  {{ $t("inbox.openLesson") }}
                </v-btn>
              </div>
            </div>

            <!-- Homework enviado pelo professor: mesmo atalho da anotação,
                 direto para a aula onde ele está (card 86akj6ghg). O texto
                 do homework vem da própria notificação; as antigas, que não
                 o traziam, ficam com a explicação genérica. -->
            <div v-if="homeworkContentId" class="ncta">
              <div class="ncta__title">{{ $t("inbox.homework") }}</div>
              <linkified-text
                v-if="homeworkText"
                tag="div"
                class="ncta__text ncta__text--homework"
                :text="homeworkText"
              />
              <div v-else class="ncta__text">
                {{ $t("inbox.homeworkText") }}
              </div>

              <div class="ncta__actions">
                <v-btn
                  dark
                  rounded
                  depressed
                  class="ncta__btn"
                  :color="theme.primary"
                  @click="openHomeworkLesson"
                >
                  <v-icon small left>mdi-book-open-page-variant-outline</v-icon>
                  {{ $t("inbox.openLesson") }}
                </v-btn>
              </div>
            </div>

            <!-- Mudança de aula feita pelo professor: já valeu, mas o aluno
                 pode discordar com uma justificativa. -->
            <div v-if="classChangeEventId" class="ncta">
              <div class="ncta__title">{{ $t("inbox.disputeTitle") }}</div>
              <div class="ncta__text">
                {{ $t("inbox.disputeText") }}
              </div>

              <template v-if="!disputeAnswered">
                <v-textarea
                  v-model="disputeJustification"
                  class="mt-3"
                  outlined
                  dense
                  rows="3"
                  counter="500"
                  maxlength="500"
                  hide-details="auto"
                  :label="$t('inbox.disputeLabel')"
                  :disabled="disputeBusy"
                />

                <div class="ncta__actions">
                  <v-btn
                    rounded
                    outlined
                    class="ncta__btn"
                    :color="theme.primary"
                    :disabled="!canSendDispute || disputeBusy"
                    :loading="disputeBusy"
                    @click="
                      $emit('dispute-class-change', {
                        eventId: classChangeEventId,
                        justification: disputeJustification.trim(),
                      })
                    "
                  >
                    <v-icon small left>mdi-alert-circle-outline</v-icon>
                    {{ $t("inbox.disputeSend") }}
                  </v-btn>
                </div>

                <div
                  v-if="disputeJustification && !canSendDispute"
                  class="ncta__hint"
                >
                  {{ $t("inbox.minChars") }}
                </div>
              </template>

              <div v-else class="ncta__feedback">
                <v-icon size="16" color="#00A379">mdi-check</v-icon>
                {{ $t("inbox.disputeSent") }}
              </div>
            </div>

            <!-- Lado do professor: avaliar a discordância de um aluno. -->
            <div v-if="disputeReviewId" class="ncta">
              <div class="ncta__title">{{ $t("inbox.reviewTitle") }}</div>
              <div class="ncta__text">
                {{ $t("inbox.reviewText") }}
              </div>
              <ul class="ncta__list">
                <li>
                  <strong>{{ $t("inbox.reviewReschedule") }}</strong>
                  <i18n path="inbox.reviewRescheduleText" tag="span">
                    <template #not>
                      <em>{{ $t("inbox.not") }}</em>
                    </template>
                  </i18n>
                </li>
                <li>
                  <strong>{{ $t("inbox.reviewKeep") }}</strong>
                  {{ $t("inbox.reviewKeepText") }}
                </li>
              </ul>
              <div class="ncta__text">
                {{ $t("inbox.reviewBoth") }}
              </div>

              <template v-if="!disputeAnswered">
                <v-textarea
                  v-model="disputeResponse"
                  class="mt-3"
                  outlined
                  dense
                  rows="2"
                  counter="500"
                  maxlength="500"
                  hide-details="auto"
                  :label="$t('inbox.reviewResponse')"
                  :disabled="disputeBusy"
                />

                <div class="ncta__actions">
                  <v-btn
                    dark
                    rounded
                    depressed
                    class="ncta__btn"
                    :color="theme.primary"
                    :disabled="disputeBusy"
                    :loading="disputeBusy"
                    @click="emitDisputeDecision(true)"
                  >
                    <v-icon small left>mdi-calendar-refresh-outline</v-icon>
                    {{ $t("inbox.reviewReschedule") }}
                  </v-btn>

                  <v-btn
                    rounded
                    outlined
                    class="ncta__btn"
                    :color="theme.primary"
                    :disabled="disputeBusy"
                    @click="emitDisputeDecision(false)"
                  >
                    <v-icon small left>mdi-calendar-check-outline</v-icon>
                    {{ $t("inbox.reviewKeep") }}
                  </v-btn>
                </div>
              </template>

              <div v-else class="ncta__feedback">
                <v-icon size="16" color="#00A379">mdi-check</v-icon>
                {{ $t("inbox.reviewDone") }}
              </div>
            </div>

            <!-- Aula geral -->
            <div v-if="generalClassInvite" class="ncta">
              <div class="ncta__title">{{ $t("inbox.confirmAttendance") }}</div>
              <div class="ncta__text">
                {{ $t("inbox.confirmAttendanceText") }}
              </div>

              <div class="ncta__actions">
                <v-btn
                  dark
                  rounded
                  depressed
                  class="ncta__btn"
                  :color="theme.primary"
                  :disabled="generalClassAnswered || generalClassBusy"
                  :loading="generalClassBusy"
                  @click="
                    $emit('confirm-general-class', {
                      sessionId: generalClassInvite,
                    })
                  "
                >
                  <v-icon small left>mdi-check</v-icon>
                  {{ $t("inbox.willAttend") }}
                </v-btn>

                <v-btn
                  rounded
                  outlined
                  class="ncta__btn"
                  :color="theme.primary"
                  :disabled="generalClassAnswered || generalClassBusy"
                  @click="
                    $emit('decline-general-class', {
                      sessionId: generalClassInvite,
                    })
                  "
                >
                  <v-icon small left>mdi-close</v-icon>
                  {{ $t("inbox.wontAttend") }}
                </v-btn>
              </div>

              <div v-if="generalClassAnswered" class="ncta__feedback">
                <v-icon size="16" color="#00A379">mdi-check</v-icon>
                {{ $t("inbox.answered") }}
              </div>
            </div>

            <!-- Nova data sugerida pelo professor (card 86aje36nz, retorno de
                 22/09): o aluno confirma o agendamento ou cancela o pedido,
                 sem sair da caixa de entrada. -->
            <div v-if="trialSuggestion" class="ncta">
              <div class="ncta__title">{{ $t("inbox.newDateTitle") }}</div>
              <div class="ncta__text">
                {{ $t("inbox.newDateText") }}
              </div>

              <div class="ncta__actions">
                <v-btn
                  dark
                  rounded
                  depressed
                  class="ncta__btn"
                  :color="theme.primary"
                  :disabled="trialAnswered || trialBusy"
                  :loading="trialBusy"
                  @click="
                    $emit('respond-trial-suggestion', {
                      classId: trialSuggestion.classId,
                      accept: true,
                    })
                  "
                >
                  <v-icon small left>mdi-check</v-icon>
                  {{ $t("inbox.confirmTrial") }}
                </v-btn>

                <v-btn
                  rounded
                  outlined
                  class="ncta__btn"
                  :color="theme.primary"
                  :disabled="trialAnswered || trialBusy"
                  @click="
                    $emit('respond-trial-suggestion', {
                      classId: trialSuggestion.classId,
                      accept: false,
                    })
                  "
                >
                  <v-icon small left>mdi-close</v-icon>
                  {{ $t("inbox.cancelRequest") }}
                </v-btn>
              </div>

              <div v-if="trialAnswered" class="ncta__feedback">
                <v-icon size="16" color="#00A379">mdi-check</v-icon>
                {{ $t("inbox.answered") }}
              </div>
            </div>

            <!-- Decisão do aluno depois da aula experimental (card 86ak6kzgj).
                 Os dois botões ficam na própria mensagem, como o card pede: a
                 decisão é a ação, não um link para outra tela. -->
            <div v-if="trialDecision" class="ncta">
              <div class="ncta__title">
                {{ $t("inbox.continueTitle") }}
              </div>
              <div class="ncta__text">
                {{ $t("inbox.continueText") }}
              </div>

              <div class="ncta__actions">
                <v-btn
                  dark
                  rounded
                  depressed
                  class="ncta__btn"
                  :color="theme.primary"
                  :disabled="trialAnswered || trialBusy"
                  :loading="trialBusy"
                  @click="
                    $emit('resolve-trial', {
                      classId: trialDecision.classId,
                      interested: true,
                    })
                  "
                >
                  <v-icon small left>mdi-check</v-icon>
                  {{ $t("inbox.wantContinue") }}
                </v-btn>

                <v-btn
                  rounded
                  outlined
                  class="ncta__btn"
                  :color="theme.primary"
                  :disabled="trialAnswered || trialBusy"
                  @click="
                    $emit('resolve-trial', {
                      classId: trialDecision.classId,
                      interested: false,
                    })
                  "
                >
                  <v-icon small left>mdi-account-search-outline</v-icon>
                  {{ $t("inbox.wantOther") }}
                </v-btn>
              </div>

              <div v-if="trialAnswered" class="ncta__feedback">
                <v-icon size="16" color="#00A379">mdi-check</v-icon>
                {{ $t("inbox.answered") }}
              </div>
            </div>
          </div>

          <div v-else class="nempty nempty--preview">
            <v-icon size="34" :color="theme.primary"
              >mdi-email-open-outline</v-icon
            >
            <div class="nempty__title">{{ $t("inbox.selectTitle") }}</div>
            <div class="nempty__text">
              {{ $t("inbox.selectText") }}
            </div>
          </div>
        </section>
      </div>

      <v-pagination
        v-if="showPagination"
        class="ninbox__pagination"
        circle
        :color="theme.primary"
        :length="totalPages"
        :value="currentPage"
        @input="$emit('page-change', $event)"
      />
    </template>
  </v-container>
</template>

<script>
import CommunicationContent from "./CommunicationContent.vue";
import LinkifiedText from "@/components/course/LinkifiedText.vue";
import { languageTag } from "@/plugins/i18n";

// Espelha JUSTIFICATION_MARKER de study-groups.service.ts (pitch-english-api).
// Mudar lá exige mudar aqui.
const JUSTIFICATION_MARKER = "Justificativa do aluno:";
// Espelha HOMEWORK_NOTIFICATION_MARKER de noteNotification.ts (pitch-english-api):
// o parágrafo que começa com ele é o texto do homework, que vai para o cartão
// "Homework da aula" em vez de ficar no corpo (card 86akj6ghg).
const HOMEWORK_MARKER = "Homework da aula:";

export default {
  name: "NotificationInbox",

  components: { CommunicationContent, LinkifiedText },

  props: {
    title: {
      type: String,
      default: "",
    },
    subtitle: {
      type: String,
      default: "",
    },
    notifications: {
      type: Array,
      default: () => [],
    },
    selectedNotification: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    totalPages: {
      type: Number,
      default: 1,
    },
    unreadCount: {
      type: Number,
      default: 0,
    },
    // Filtro "não lidas" ligado: a lista já vem filtrada do servidor.
    unreadOnly: {
      type: Boolean,
      default: false,
    },
    // Abas de categoria vindas da API: { key, label, icon, unread }.
    categories: {
      type: Array,
      default: () => [],
    },
    // null = "Todas".
    activeCategory: {
      type: String,
      default: null,
    },
    voted: {
      type: Boolean,
      default: false,
    },
    votingBusy: {
      type: Boolean,
      default: false,
    },
    generalClassAnswered: {
      type: Boolean,
      default: false,
    },
    generalClassBusy: {
      type: Boolean,
      default: false,
    },
    disputeAnswered: {
      type: Boolean,
      default: false,
    },
    disputeBusy: {
      type: Boolean,
      default: false,
    },
    // Decisão depois da aula experimental (card 86ak6kzgj).
    trialAnswered: {
      type: Boolean,
      default: false,
    },
    trialBusy: {
      type: Boolean,
      default: false,
    },
    // Aulas extras (card 86ajzet19). O componente e compartilhado entre aluno e
    // professor e nao fala com a API: quem chama os endpoints e a view pai, e o
    // resultado volta por estas props.
    extraLessonsPreview: {
      type: Array,
      default: null,
    },
    extraLessonsBusy: {
      type: Boolean,
      default: false,
    },
    extraLessonsIncomplete: {
      type: Boolean,
      default: false,
    },
    // Pergunta ja respondida: { status, scheduledQuantity }, vindo da API.
    // Nulo enquanto a pergunta estiver aberta.
    extraLessonsDecision: {
      type: Object,
      default: null,
    },
    theme: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      sharingLevel: false,
      disputeJustification: "",
      disputeResponse: "",
      // Atalhos de quantidade pedidos no card, mais um campo livre.
      extraLessonQuantities: [5, 10, 15],
      customQuantity: null,
      pendingQuantity: null,
    };
  },

  watch: {
    // Cada notificação tem a sua discordância. Trocar de mensagem sem limpar
    // deixaria o texto escrito para uma aula anexado à seguinte.
    selectedNotification() {
      this.disputeJustification = "";
      this.disputeResponse = "";
      this.customQuantity = null;
      this.pendingQuantity = null;
      this.$emit("reset-extra-lessons");
    },
  },

  computed: {
    // "Todas" na frente das categorias da API. O contador dela é o total de
    // não lidas do usuário (prop), não a soma das abas: uma notificação de
    // categoria que o perfil não lista continua aparecendo em "Todas".
    categoryTabs() {
      if (!this.categories.length) return [];

      return [
        {
          key: null,
          label: this.$t("inbox.all"),
          icon: "mdi-inbox-outline",
          unread: this.unreadCount,
        },
        ...this.categories,
      ];
    },
    // O corpo da notificação vem em parágrafos separados por linha em branco.
    // O parágrafo que começa com JUSTIFICATION_MARKER carrega o motivo escrito
    // pelo aluno e vira citação — na mesma fonte do resto o professor não
    // achava a justificativa no meio do texto.
    descriptionBlocks() {
      const description = this.selectedNotification?.description;

      if (!description) {
        return [{ quote: false, text: this.$t("inbox.noDescription") }];
      }

      return (
        String(description)
          .split(/\n{2,}/)
          .map((block) => block.trim())
          // O texto do homework aparece no cartão dele, não duplicado no corpo.
          .filter((block) => block && !block.startsWith(HOMEWORK_MARKER))
          .map((block) => {
            if (!block.startsWith(JUSTIFICATION_MARKER)) {
              return { quote: false, text: block };
            }

            const text = block.slice(JUSTIFICATION_MARKER.length).trim();

            return {
              quote: true,
              // O marcador é o texto que a API grava (em português); o rótulo
              // exibido segue o idioma da tela.
              label: this.$t("inbox.justification"),
              // As aspas vêm do backend; a citação já é o destaque visual.
              text: text.replace(/^"|"$/g, ""),
            };
          })
      );
    },
    // Mudança de aula aplicada pelo professor: o aluno pode discordar.
    classChangeEventId() {
      const reference = this.selectedNotification?.reference;
      if (!reference) return null;
      const match = String(reference).match(/^class-change:(.+):dispute$/);
      return match ? match[1] : null;
    },
    // Discordância aguardando a avaliação do professor.
    disputeReviewId() {
      const reference = this.selectedNotification?.reference;
      if (!reference) return null;
      const match = String(reference).match(/^class-dispute:([^:]+):review$/);
      return match ? match[1] : null;
    },
    // Espelha o `@MinLength(10)` do DTO para não gastar um round-trip com um
    // texto que a API já vai recusar.
    canSendDispute() {
      return this.disputeJustification.trim().length >= 10;
    },
    generalClassInvite() {
      const reference = this.selectedNotification?.reference;
      if (!reference) return null;
      const match = String(reference).match(/^general-class:([^:]+):invite/);
      return match ? match[1] : null;
    },
    // Decisão do aluno depois da aula experimental (card 86ak6kzgj). A
    // referência que a API grava é `trial-decision:<turma>:<aluno>`; o aluno
    // é sempre quem está lendo, então só a turma interessa aqui.
    //
    // A notificação do professor usa o prefixo `trial-done:` e não cai neste
    // regex de propósito: ele não responde, ele aceita pela página da turma.
    trialDecision() {
      const reference = this.selectedNotification?.reference;
      if (!reference) return null;

      const match = String(reference).match(/^trial-decision:([^:]+):([^:]+)$/);

      return match ? { classId: match[1], customerId: match[2] } : null;
    },
    // Nova data sugerida pelo professor: `trial-suggestion:<turma>:<data>:<hora>`.
    trialSuggestion() {
      const reference = this.selectedNotification?.reference;
      if (!reference) return null;

      const match = String(reference).match(
        /^trial-suggestion:([^:]+):(\d{4}-\d{2}-\d{2}):(\d{2}:\d{2})$/
      );

      return match
        ? { classId: match[1], date: match[2], from: match[3] }
        : null;
    },
    // `customer-level:<aluno>:<nível>`: níveis 1 a 11 têm imagem de
    // comemoração (a trilha de conversação e o nível 0 não têm).
    levelUpImage() {
      const reference = this.selectedNotification?.reference;
      if (!reference) return null;
      const match = String(reference).match(/^customer-level:[^:]+:(d+)$/);
      const level = match ? Number(match[1]) : 0;
      return level >= 1 && level <= 11 ? `/img/level-up/${level}.jpg` : null;
    },
    // `attendance-pending:<aula>:<turma>`: a turma é o `:id` da página de
    // gestão dela (card 17trt6jfxt2).
    attendancePendingClassId() {
      const reference = this.selectedNotification?.reference;
      if (!reference) return null;
      const match = String(reference).match(
        /^attendance-pending:[^:]+:([^:]+)$/
      );
      return match ? match[1] : null;
    },
    // `study-plan:<plano>` (plano liberado) e `study-plan-grade:<item>`.
    isStudyPlanNotice() {
      return /^study-plan(-grade)?:/.test(
        String(this.selectedNotification?.reference || "")
      );
    },
    // `teacher-extra:<nota>:<aluno>:<lote>`: conteúdo extra indicado.
    isTeacherExtraNotice() {
      return /^teacher-extra:/.test(
        String(this.selectedNotification?.reference || "")
      );
    },
    // Anotação compartilhada pelo professor: a referência carrega o id do
    // conteúdo, que é o mesmo `:id` da rota da aula. Card 86ak5yua7.
    teacherNoteContentId() {
      const reference = this.selectedNotification?.reference;
      if (!reference) return null;
      const match = String(reference).match(/^teacher-note:([^:]+):/);
      return match ? match[1] : null;
    },
    // Texto do homework, no parágrafo marcado da descrição. Só vale para a
    // notificação de homework: nas outras o marcador não tem significado.
    homeworkText() {
      if (!this.homeworkContentId) return "";

      const description = String(this.selectedNotification?.description || "");
      const block = description
        .split(/\n{2,}/)
        .map((item) => item.trim())
        .find((item) => item.startsWith(HOMEWORK_MARKER));

      return block ? block.slice(HOMEWORK_MARKER.length).trim() : "";
    },
    // Homework: `homework:<conteúdo>:<nota>:<aluno>`, com o mesmo id de
    // conteúdo da anotação. A referência é separada de propósito, então o
    // atalho precisa ler as duas (card 86akj6ghg).
    homeworkContentId() {
      const reference = this.selectedNotification?.reference;
      if (!reference) return null;
      const match = String(reference).match(/^homework:([^:]+):/);
      return match ? match[1] : null;
    },
    // Pedido de entrada numa turma: a referência carrega a turma, que é o
    // `:id` da página de gestão dela (card 86akj7x9f).
    groupRequestClassId() {
      const reference = this.selectedNotification?.reference;
      if (!reference) return null;
      const match = String(reference).match(/^group-request:([^:]+):/);
      return match ? match[1] : null;
    },
    // Recusa do pedido (`group-join:<turma>:<aluno>:3[:<vinculo>]`) ou
    // remoção da turma (`group-removed:`): o aluno volta ao match.
    backToMatch() {
      const reference = String(this.selectedNotification?.reference || "");
      return (
        /^group-removed:/.test(reference) ||
        /^group-join:[^:]+:[^:]+:3(:|$)/.test(reference)
      );
    },
    // Pergunta de aulas extras no fim do nível (card 86ajzet19). A referência
    // traz o prompt e a turma, então a tela não precisa de outra consulta.
    extraLessonsPrompt() {
      const reference = this.selectedNotification?.reference;
      if (!reference) return null;

      const match = String(reference).match(
        /^extra-lessons:([^:]+):([^:]+):prompt$/
      );

      return match ? { promptId: match[1], classId: match[2] } : null;
    },
    extraLessonsDecisionText() {
      const decision = this.extraLessonsDecision;
      if (!decision) return "";

      if (decision.status === "SCHEDULED") {
        const total = Number(decision.scheduledQuantity) || 0;
        return total
          ? this.$tc("inbox.extraScheduled", total, { count: total })
          : this.$t("inbox.extraScheduledGeneric");
      }

      if (decision.status === "DECLINED") {
        return this.$t("inbox.extraDeclined");
      }

      // SUPERSEDED: a grade mudou e uma pergunta mais nova tomou o lugar.
      return this.$t("inbox.extraSuperseded");
    },
    rootStyle() {
      return {
        "--nf-accent": this.theme.primary,
        "--nf-soft": this.theme.surfaceAlt || this.theme.surface,
        "--nf-border": this.theme.border,
      };
    },
    showPagination() {
      return this.totalPages > 1;
    },
    validCustomQuantity() {
      const total = Number(this.customQuantity);
      return Number.isInteger(total) && total >= 1 && total <= 40;
    },
  },

  methods: {
    // A API recusa fora da faixa, mas deixar o botão ativo para um valor
    // inválido é convidar ao erro.
    requestExtraLessonsPreview(quantity) {
      const total = Number(quantity);

      if (!Number.isInteger(total) || total < 1 || total > 40) return;

      this.pendingQuantity = total;
      this.$emit("preview-extra-lessons", {
        classId: this.extraLessonsPrompt.classId,
        quantity: total,
      });
    },
    // "ter 18/08 — 20:00": o dia da semana ajuda o professor a reconhecer o
    // padrão da turma de relance.
    slotLabel(slot) {
      const date = new Date(slot.date + "T" + slot.startHour + ":00");
      const weekDay = date.toLocaleDateString(languageTag(), {
        weekday: "short",
      });
      const dayMonth = date.toLocaleDateString(languageTag(), {
        day: "2-digit",
        month: "2-digit",
      });

      return weekDay + " " + dayMonth + " — " + slot.startHour;
    },
    // A caixa é compartilhada entre professor e aluno; a rota da aula só
    // existe do lado do aluno, que é quem recebe anotação compartilhada.
    // Por path: só o aluno autônomo recebe esses avisos.
    openTeacherExtras() {
      this.$router.push("/connect/aluno/curso/indicados").catch(() => null);
    },
    openStudyPlan() {
      this.$router.push("/connect/aluno/plano-de-estudos").catch(() => null);
    },
    openTeacherNoteLesson() {
      if (!this.teacherNoteContentId) return;

      this.$router.push(`/connect/aluno/curso/${this.teacherNoteContentId}`);
    },
    openHomeworkLesson() {
      if (!this.homeworkContentId) return;

      this.$router.push(`/connect/aluno/curso/${this.homeworkContentId}`);
    },
    // Por path: o nome "Turmas" também existe na área do professor.
    openMatch() {
      this.$router.push("/connect/aluno/turmas");
    },
    // Mesmo caminho do certificado: menu do aparelho no celular; no
    // computador baixa a imagem e abre o compositor do LinkedIn.
    async shareLevelUp() {
      const caption = this.$t("inbox.levelUpCaption");
      const touch = window.matchMedia("(pointer: coarse)").matches;
      const tab = touch ? null : window.open("", "_blank");
      this.sharingLevel = true;

      try {
        const res = await fetch(this.levelUpImage);
        const blob = await res.blob();
        const file = new File([blob], "conquista-pitch.jpg", {
          type: "image/jpeg",
        });

        if (
          touch &&
          typeof navigator.canShare === "function" &&
          navigator.canShare({ files: [file] })
        ) {
          await navigator.share({ files: [file], text: caption });
          return;
        }

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "conquista-pitch.jpg";
        link.click();
        URL.revokeObjectURL(link.href);
        if (tab) {
          tab.location.href = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(
            caption
          )}`;
        }
        this.$root.$emit("display-alert", {
          text: this.$t("inbox.levelUpShared"),
          type: 0,
        });
      } catch (error) {
        if (tab) tab.close();
        if (error?.name === "AbortError") return;
        this.$root.$emit("display-alert", {
          text: this.$t("inbox.levelUpShareError"),
          type: 1,
        });
      } finally {
        this.sharingLevel = false;
      }
    },
    openAttendanceClass() {
      if (!this.attendancePendingClassId) return;

      this.$router.push(
        `/connect/professor/turmas/${this.attendancePendingClassId}`
      );
    },
    openRequestedGroup() {
      if (!this.groupRequestClassId) return;

      this.$router.push(
        `/connect/professor/turmas/${this.groupRequestClassId}`
      );
    },
    emitDisputeDecision(reschedule) {
      this.$emit("resolve-dispute", {
        disputeId: this.disputeReviewId,
        reschedule,
        response: this.disputeResponse.trim(),
      });
    },
    // Card 86akqxu0h: 🟡 Pendente / 🟢 Respondido / ⚪ Encerrado.
    surveyBadge(item) {
      const status = item?.communication?.survey?.status;
      if (status === "answered") return this.$t("inbox.surveyAnswered");
      if (status === "closed") return this.$t("inbox.surveyClosed");
      if (status === "pending") return this.$t("inbox.surveyPending");
      return "";
    },
    snippet(description) {
      if (!description) return this.$t("inbox.noDescription");

      const normalized = description.replace(/\s+/g, " ").trim();

      return normalized.length > 82
        ? `${normalized.slice(0, 79)}...`
        : normalized;
    },
  },
};
</script>

<style scoped lang="css">
.ninbox {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --paper: var(--dk-paper, #faf7fc);
  --line: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  color: var(--ink);
}

/* ---------- Cabeçalho ---------- */
.ninbox__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin: 20px 0 22px;
  flex-wrap: wrap;
}
.ninbox__eyebrow {
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nf-accent);
}
.ninbox__title {
  margin: 4px 0 0;
  font-family: "Poppins", sans-serif;
  font-size: clamp(1.9rem, 4vw, 2.6rem);
  font-weight: 700;
  line-height: 1.04;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.ninbox__sub {
  margin: 8px 0 0;
  max-width: 52ch;
  font-size: 0.9rem;
  color: var(--muted);
  line-height: 1.5;
}
.ninbox__count {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  border-radius: 999px;
  background: var(--nf-soft);
  color: var(--nf-accent);
  font-size: 0.82rem;
  font-weight: 700;
  white-space: nowrap;
}
.ninbox__count-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}
.ninbox__count--zero {
  background: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.06);
  color: var(--muted);
}

/* ---------- Categorias ---------- */
.ncats {
  display: flex;
  gap: 8px;
  margin: 0 0 18px;
  padding-bottom: 4px;
  overflow-x: auto;
  scrollbar-width: thin;
}
.ncat {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  padding: 7px 14px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: var(--dk-surface, #fff);
  color: var(--muted);
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease;
}
.ncat:hover {
  border-color: var(--nf-accent);
  color: var(--nf-accent);
}
.ncat--active {
  background: var(--nf-accent);
  border-color: var(--nf-accent);
  color: #fff;
}
.ncat--active .ncat__icon,
.ncat--active:hover {
  color: #fff;
}
.ncat__icon {
  color: inherit !important;
}
.ncat__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--nf-soft);
  color: var(--nf-accent);
  font-size: 0.68rem;
  font-weight: 800;
}
.ncat--active .ncat__badge {
  background: rgba(255, 255, 255, 0.24);
  color: #fff;
}
.nrow__cat {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
  padding: 2px 9px;
  border-radius: 999px;
  background: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.06);
  color: var(--muted);
  font-size: 0.66rem;
  font-weight: 700;
  white-space: nowrap;
}
.nrow__cat .v-icon {
  color: inherit !important;
}

/* ---------- Shell ---------- */
.ninbox__shell {
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
  border-radius: 22px;
  overflow: hidden;
  box-shadow: 0 22px 48px -40px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.55);
}
.ninbox__pane--list {
  border-right: 1px solid var(--line);
  background: var(--paper);
  min-width: 0;
}
.ninbox__pane-head {
  padding: 22px 22px 16px;
  border-bottom: 1px solid var(--line);
}
.ninbox__pane-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--ink);
}
.ninbox__pane-sub {
  margin-top: 3px;
  font-size: 0.8rem;
  color: var(--muted);
}

/* ---------- Linha da lista ---------- */
.ninbox__list {
  padding: 12px;
  max-height: 620px;
  overflow-y: auto;
}
.nrow {
  position: relative;
  width: 100%;
  display: flex;
  margin-bottom: 8px;
  border: 1px solid transparent;
  border-radius: 14px;
  background: var(--dk-surface, #fff);
  cursor: pointer;
  text-align: left;
  overflow: hidden;
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    background-color 0.15s ease;
}
.nrow:hover {
  border-color: var(--line);
  box-shadow: 0 14px 26px -22px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.5);
}
.nrow:focus-visible {
  outline: 2px solid var(--nf-accent);
  outline-offset: 2px;
}
.nrow__rail {
  flex: 0 0 3px;
  background: transparent;
}
.nrow--selected {
  border-color: var(--nf-border);
  background: var(--nf-soft);
}
.nrow--selected .nrow__rail {
  background: var(--nf-accent);
}
.nrow__body {
  flex: 1 1 auto;
  min-width: 0;
  padding: 14px 16px;
}
.nrow__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.nrow__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--dk-ink-soft, #4b4551);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nrow--unread .nrow__title {
  color: var(--ink);
  font-weight: 800;
}
.nrow__dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--nf-accent);
}
.nrow__date {
  flex: 0 0 auto;
  font-size: 0.73rem;
  color: var(--muted);
  white-space: nowrap;
}
.nrow__bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 8px;
}
.nrow__snippet {
  font-size: 0.82rem;
  color: var(--muted);
  line-height: 1.45;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.npv__survey {
  display: inline-block;
  margin: 4px 0 10px;
  padding: 3px 10px;
  border-radius: 999px;
  background: var(--nf-soft);
  color: var(--nf-accent);
  font-size: 0.78rem;
  font-weight: 700;
}
.nrow__tag {
  flex: 0 0 auto;
  padding: 2px 9px;
  border-radius: 999px;
  background: var(--nf-soft);
  color: var(--nf-accent);
  font-size: 0.66rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

/* ---------- Preview ---------- */
.ninbox__pane--preview {
  padding: 26px;
  min-width: 0;
}
.npv {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.npv__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.npv__head-main {
  min-width: 0;
}
.npv__title {
  font-size: 1.35rem;
  font-weight: 800;
  line-height: 1.25;
  color: var(--ink);
}
.npv__meta {
  margin-top: 6px;
  font-size: 0.82rem;
  color: var(--muted);
}
.npv__status {
  flex: 0 0 auto;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.nfilter {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding: 6px 12px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: var(--dk-surface, #fff);
  color: var(--muted);
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease;
}
.nfilter:hover {
  border-color: var(--nf-accent);
  color: var(--nf-accent);
}
.nfilter--active,
.nfilter--active:hover {
  background: var(--nf-accent);
  border-color: var(--nf-accent);
  color: #fff;
}
.nfilter__icon {
  color: inherit !important;
}
.npv__flags {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.npv__toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: var(--dk-surface, #fff);
  color: var(--muted);
  font-size: 0.74rem;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.18s ease, border-color 0.18s ease;
}
.npv__toggle:hover {
  border-color: var(--nf-accent);
  color: var(--nf-accent);
}
.npv__toggle-icon {
  color: inherit !important;
}
.npv__status--unread {
  background: var(--nf-soft);
  color: var(--nf-accent);
}
.npv__status--read {
  background: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.06);
  color: var(--muted);
}
.npv__paragraph {
  margin: 0 0 12px;
}

/* Prévia das aulas extras: lista de leitura, não tabela — o professor confere
   datas em sequência antes de confirmar. */
.xtra__list {
  list-style: none;
  margin: 12px 0;
  padding: 0;
  max-height: 240px;
  overflow-y: auto;
}

.xtra__item {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 0;
  border-bottom: 1px solid var(--nf-border);
}

.xtra__item:last-child {
  border-bottom: 0;
}

.xtra__date {
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--dk-ink, #2f2838);
  white-space: nowrap;
}

.xtra__subject {
  font-size: 0.82rem;
  color: var(--dk-muted, #6b6472);
  text-align: right;
}

.xtra__subject--empty {
  font-style: italic;
  color: var(--dk-faint, #9b93a3);
}

.npv__paragraph:last-child {
  margin-bottom: 0;
}

/* A justificativa do aluno sai da fonte corrida: borda, recuo e itálico para
   o professor achar o motivo sem varrer o parágrafo inteiro. */
.npv__quote {
  display: block;
  margin: 0 0 12px;
  padding: 12px 16px;
  border-left: 3px solid var(--nf-accent);
  border-radius: 0 10px 10px 0;
  background: var(--nf-soft);
}

.npv__quote-label {
  display: block;
  margin-bottom: 4px;
  color: var(--nf-accent);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.npv__quote-text {
  display: block;
  color: var(--dk-ink, #2f2838);
  font-size: 1rem;
  font-style: italic;
  line-height: 1.6;
}

.npv__body {
  padding: 22px 24px;
  border-radius: 16px;
  border: 1px solid var(--line);
  background: var(--paper);
  color: var(--dk-ink-soft, #4b4551);
  font-size: 0.95rem;
  line-height: 1.75;
  white-space: pre-line;
}

/* ---------- Blocos de ação (votação / aula geral) ---------- */
.ncta {
  padding: 22px;
  border: 1px solid var(--line);
  border-left: 3px solid var(--nf-accent);
  border-radius: 16px;
  background: var(--dk-surface, #fff);
}
.ncta__title {
  font-size: 1rem;
  font-weight: 800;
  color: var(--ink);
}
.ncta__text {
  margin-top: 6px;
  color: var(--muted);
  line-height: 1.55;
  font-size: 0.88rem;
}
.ncta__text--homework {
  color: var(--ink);
  font-size: 0.95rem;
  white-space: pre-line;
  overflow-wrap: anywhere;
}
.ncta__list {
  margin: 8px 0 0;
  padding-left: 18px;
  color: var(--muted);
  line-height: 1.55;
  font-size: 0.88rem;
}
.ncta__list li + li {
  margin-top: 5px;
}
.ncta__list strong {
  color: var(--ink);
  font-weight: 700;
}
.ncta__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
}
.ncta--level {
  text-align: center;
}
.ncta__level-img {
  display: block;
  width: 100%;
  max-width: 280px;
  margin: 0 auto 14px;
  border-radius: 14px;
}
.ncta__btn {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 700;
}
.ncta__hint {
  margin-top: 8px;
  font-size: 0.8rem;
  color: var(--muted);
}
.ncta__feedback {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 14px;
  font-size: 0.83rem;
  font-weight: 700;
  color: var(--dk-teal-deep, #007f5e);
}

/* ---------- Estados vazios ---------- */
.nempty {
  min-height: 320px;
  padding: 48px 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
}
.nempty__title {
  margin-top: 16px;
  font-size: 1rem;
  font-weight: 700;
  color: var(--ink);
}
.nempty__text {
  margin-top: 6px;
  max-width: 320px;
  font-size: 0.88rem;
  color: var(--muted);
  line-height: 1.55;
}

.ninbox__pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 22px;
}

/* ---------- Responsivo ---------- */
@media (max-width: 900px) {
  .ninbox__shell {
    grid-template-columns: 1fr;
  }
  .ninbox__pane--list {
    border-right: none;
    border-bottom: 1px solid var(--line);
  }
  .ninbox__list {
    max-height: 380px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .nrow {
    transition: none;
  }
}
</style>
